import { useCallback, useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import { post, upload, fileUrl, WEB_BASE } from '../../api';

const PAGE_SIZE = 20;

const STATUS = {
  0: { text: '审核中', color: 'orange' },
  1: { text: '审核通过', color: 'green' },
  2: { text: '审核拒绝', color: 'red' },
};

const formatTime = (value) => (value ? value.replace('T', ' ').slice(0, 19) : '-');

const statusTag = (value) => {
  const item = STATUS[value];
  return item ? <Tag color={item.color}>{item.text}</Tag> : '-';
};

/** 图片字段：上传后只保存 file id，展示走缩略图 */
function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const rsp = await upload('/file/upload', file);
      if (rsp.code) {
        message.error(rsp.msg || '上传失败');
        return;
      }
      onChange(rsp.data.id);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space align="start" size={12}>
      {value ? (
        <div>
          <Image width={120} src={fileUrl(value, 720)} preview={{ src: fileUrl(value, 1080) }} />
          <div>
            <Button type="link" size="small" danger onClick={() => onChange('')}>
              移除
            </Button>
          </div>
        </div>
      ) : null}
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={(file) => {
          if (!/^image\//.test(file.type || '')) {
            message.error('只能上传图片文件');
            return Upload.LIST_IGNORE;
          }
          doUpload(file);
          return false;
        }}
      >
        <Button icon={<PlusOutlined />} loading={uploading}>
          {value ? '重新上传' : '上传图片'}
        </Button>
      </Upload>
    </Space>
  );
}

export default function Stores() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [disabledId, setDisabledId] = useState(null);
  const [reviewStore, setReviewStore] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [detailStore, setDetailStore] = useState(null);
  const [editStore, setEditStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [keyword, setKeyword] = useState('');

  const fetchStores = useCallback(async (current) => {
    setLoading(true);
    try {
      const rsp = await post('/admin/store/list', { page: current, size: PAGE_SIZE, keyword: keyword || undefined });
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setList(rsp.data.list || []);
      setTotal(rsp.data.total || 0);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    fetchStores(page);
  }, [page, fetchStores]);

  const toggleDisabled = async (store, disabled) => {
    setDisabledId(store.id);
    try {
      const rsp = await post('/admin/store/disabled', { id: store.id, disabled });
      if (rsp.code) {
        message.error(rsp.msg || '操作失败');
        return;
      }
      message.success(disabled === 1 ? '已禁用' : '已启用');
      fetchStores(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setDisabledId(null);
    }
  };

  const review = async (status) => {
    setReviewing(true);
    try {
      const rsp = await post('/admin/store/status', { id: reviewStore.id, status });
      if (rsp.code) {
        message.error(rsp.msg || '操作失败');
        return;
      }
      message.success(status === 1 ? '已审核通过' : '已审核拒绝');
      setReviewStore(null);
      fetchStores(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setReviewing(false);
    }
  };

  const openEdit = (record) => {
    setEditStore(record);
    editForm.setFieldsValue({
      name: record.name || '',
      realName: record.realName || '',
      idNumber: record.idNumber || '',
      country: record.country || '',
      address: record.address || '',
      document: record.document || '',
      license: record.license || '',
    });
  };

  const submitEdit = async (values) => {
    setSaving(true);
    try {
      const rsp = await post('/admin/store/update', { id: editStore.id, ...values });
      if (rsp.code) {
        message.error(rsp.msg || '保存失败');
        return;
      }
      message.success('店铺资料已保存');
      setEditStore(null);
      fetchStores(page);
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '店铺 Logo',
      dataIndex: 'logo',
      align: 'center',
      width: 70,
      render: (value) =>
        value ? (
          <img
            src={fileUrl(value, 480)}
            alt="logo"
            style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: '店铺名称',
      dataIndex: 'name',
      align: 'center',
      width: 140,
      render: (value, record) => (
        <a
          href={`${WEB_BASE}/store?id=${record.id}`}
          target="_blank"
          rel="noreferrer"
          style={{ color: '#1677ff' }}
        >
          {value}
        </a>
      ),
    },
    { title: '邮箱', dataIndex: 'email', align: 'center', render: (value) => value || '-' },
    {
      title: '店铺 ID',
      dataIndex: 'id',
      align: 'center',
      width: 200,
      render: (value) => (
        <span
          title={value}
          style={{
            display: 'inline-block',
            fontFamily: 'Menlo, Consolas, monospace',
            fontSize: 11,
            lineHeight: '15px',
            color: '#666',
            wordBreak: 'break-all',
          }}
        >
          {value}
        </span>
      ),
    },
    { title: '真实姓名', dataIndex: 'realName', align: 'center', width: 100, render: (value) => value || '-' },
    { title: '国家', dataIndex: 'country', align: 'center', width: 90, render: (value) => value || '-' },
    { title: '店铺地址', dataIndex: 'address', align: 'center', render: (value) => value || '-' },
    {
      title: '审核状态',
      dataIndex: 'status',
      align: 'center',
      width: 100,
      render: (value, record) => (
        <Button
          type="text"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          title="点击修改审核状态"
          onClick={() => setReviewStore(record)}
        >
          {statusTag(value)}
        </Button>
      ),
    },
    {
      title: '禁用状态',
      dataIndex: 'disabled',
      align: 'center',
      width: 90,
      render: (value, record) => (
        <Switch
          checked={value === 0}
          checkedChildren="正常"
          unCheckedChildren="禁用"
          loading={disabledId === record.id}
          onChange={(checked) => toggleDisabled(record, checked ? 0 : 1)}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      align: 'center',
      width: 160,
      render: formatTime,
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => setDetailStore(record)}>
            详情
          </Button>
          <Button type="link" size="small" onClick={() => openEdit(record)}>
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="list-toolbar">
        <div className="list-title">店铺列表</div>
        <Input.Search
          allowClear
          placeholder="搜索店铺名称 / 邮箱 / 姓名 / 证件号 / 地址"
          style={{ width: 320 }}
          onSearch={(value) => {
            setKeyword(value.trim());
            setPage(1);
          }}
        />
      </div>

      <Table
        rowKey="id"
        bordered
        columns={columns}
        dataSource={list}
        loading={loading}
        className="list-table"
        tableLayout="fixed"
        scroll={{ x: 1500, y: 'calc(100vh - 300px)' }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count) => `共 ${count} 条`,
          onChange: setPage,
        }}
      />

      <Modal
        title="审核店铺"
        open={!!reviewStore}
        onCancel={() => setReviewStore(null)}
        footer={[
          <Button
            key="reject"
            danger
            loading={reviewing}
            disabled={reviewStore?.status === 2}
            onClick={() => review(2)}
          >
            审核驳回
          </Button>,
          <Button
            key="pass"
            type="primary"
            loading={reviewing}
            disabled={reviewStore?.status === 1}
            onClick={() => review(1)}
          >
            审核通过
          </Button>,
        ]}
      >
        <p>店铺：{reviewStore?.name}</p>
        <p>当前状态：{statusTag(reviewStore?.status)}</p>
        <p>可随时修改审核结果，保存后立即生效。</p>
      </Modal>

      <Modal
        title="编辑店铺资料"
        open={!!editStore}
        destroyOnClose
        width={620}
        confirmLoading={saving}
        onCancel={() => setEditStore(null)}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={submitEdit}>
          <Form.Item
            name="name"
            label="店铺名称"
            rules={[
              { required: true, message: '请输入店铺名称' },
              { max: 100, message: '店铺名称不能超过 100 个字符' },
            ]}
          >
            <Input maxLength={100} placeholder="请输入店铺名称" />
          </Form.Item>
          <Space size={12} style={{ width: '100%' }}>
            <Form.Item name="realName" label="真实姓名" style={{ width: 270 }}>
              <Input maxLength={100} placeholder="真实姓名" />
            </Form.Item>
            <Form.Item name="idNumber" label="证件号码" style={{ width: 270 }}>
              <Input maxLength={64} placeholder="身份证 / 护照号" />
            </Form.Item>
          </Space>
          <Space size={12} style={{ width: '100%' }}>
            <Form.Item name="country" label="国家" style={{ width: 270 }}>
              <Input maxLength={64} placeholder="国家" />
            </Form.Item>
            <Form.Item name="address" label="店铺地址" style={{ width: 270 }}>
              <Input maxLength={255} placeholder="店铺地址" />
            </Form.Item>
          </Space>
          <Form.Item name="document" label="证件正面照">
            <ImageField />
          </Form.Item>
          <Form.Item name="license" label="营业执照">
            <ImageField />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer title="店铺详情" width={520} open={!!detailStore} onClose={() => setDetailStore(null)}>
        {detailStore && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="店铺 Logo">
              {detailStore.logo ? (
                <Image
                  width={120}
                  src={fileUrl(detailStore.logo, 720)}
                  preview={{ src: fileUrl(detailStore.logo, 1080) }}
                />
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="店铺名称">{detailStore.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{detailStore.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="真实姓名">{detailStore.realName || '-'}</Descriptions.Item>
            <Descriptions.Item label="国家">{detailStore.country || '-'}</Descriptions.Item>
            <Descriptions.Item label="店铺地址">{detailStore.address || '-'}</Descriptions.Item>
            <Descriptions.Item label="证件号">{detailStore.idNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="邀请码">{detailStore.inviteCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="证件正面照">
              {detailStore.document ? (
                <Image
                  width={120}
                  src={fileUrl(detailStore.document, 720)}
                  preview={{ src: fileUrl(detailStore.document, 1080) }}
                />
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="营业执照">
              {detailStore.license ? (
                <Image
                  width={120}
                  src={fileUrl(detailStore.license, 720)}
                  preview={{ src: fileUrl(detailStore.license, 1080) }}
                />
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="审核状态">{statusTag(detailStore.status)}</Descriptions.Item>
            <Descriptions.Item label="禁用状态">
              {detailStore.disabled === 1 ? (
                <Tag color="red">已禁用</Tag>
              ) : (
                <Tag color="green">正常</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatTime(detailStore.createTime)}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </>
  );
}
